/**
 * Project-scoped tools for TASK-LESS sessions (headless audits on ad-hoc
 * pods). Everything is keyed on the projectId the runner was booted with —
 * there is no task, so none of these resolve state from the session's task.
 *
 * Two families:
 * - Tag-audit apply surface: list/create/update tags, suggestions, project chat.
 * - Task-audit evidence + reporting: cross-task reads (task, chat, execution
 *   logs) and reportTaskAuditResult persistence.
 */
import { z } from "zod";
import type { AgentSessionServiceMethods } from "@project/shared";
import {
  CONTEXT_LINK_LOCATOR_MAX,
  TAG_DESCRIPTION_MAX,
  TAG_OVERVIEW_MAX,
  TAG_REASON_MAX,
} from "@project/shared";
import {
  createSuggestionContract,
  getTagContract,
  listTagsContract,
  searchTasksContract,
} from "@project/shared/tool-contracts";
import { defineTool } from "../harness/index.js";
import {
  formatContextPathProblems,
  verifyContextPaths,
  type ContextPathInput,
} from "../execution/context-path-verifier.js";
import { defineContractTool } from "./contract-tool.js";
import { textResult } from "./helpers.js";

/**
 * The one connection capability these tools need — satisfied by the real
 * AgentConnection and by the adhoc runner's injectable AdhocRunnerConnection.
 */
export interface ProjectToolsConnection {
  call<M extends keyof AgentSessionServiceMethods>(
    method: M,
    payload: AgentSessionServiceMethods[M]["payload"],
  ): Promise<AgentSessionServiceMethods[M]["response"]>;
}

const CONTEXT_PATH_SHAPE = z
  .object({
    type: z
      .enum(["rule", "doc", "file", "folder"])
      .describe(
        "Link kind — all paths are repo-relative; doc marks a synced project doc, which resolves from the workspace like rule/file",
      ),
    path: z.string().min(1).max(500).describe("Repo-relative path"),
    label: z.string().max(100).optional(),
    locator: z
      .string()
      .min(1)
      .max(CONTEXT_LINK_LOCATOR_MAX)
      .regex(/^[^\r\n]*$/, "Locator cannot contain line breaks")
      .optional()
      .describe(
        'Verified-link tether: text that must keep existing in the file. With locatorType "test" it must appear inside a real it/test/describe TITLE; with "code" anywhere in the file. Validated at write time against the checkout and re-checked by the periodic sweep — a rename/delete flags the link stale. Locators containing <> are placeholders and never checked.',
      ),
    locatorType: z
      .enum(["test", "code"])
      .optional()
      .describe('How the locator must match — required iff locator is set; not valid on folder links'),
  })
  .refine((link) => (link.locator === undefined) === (link.locatorType === undefined), {
    message: "locator and locatorType must be provided together",
  })
  .refine((link) => link.locator === undefined || link.type !== "folder", {
    message: "folder links cannot carry a locator",
  });

function errText(prefix: string, error: unknown): { content: { type: "text"; text: string }[] } {
  return textResult(`${prefix}: ${error instanceof Error ? error.message : "Unknown error"}`);
}

/**
 * Gate contextPaths on the workspace checkout before they reach the API. The
 * API cannot stat the repo, so this is the only place a typo can be caught at
 * write time. Returns a rejection to hand back to the agent, or null to
 * proceed. Validation is skipped when the caller has no workspace path.
 */
async function rejectBadContextPaths(
  contextPaths: ContextPathInput[] | undefined,
  workspaceDir: string | undefined,
): Promise<{ content: { type: "text"; text: string }[] } | null> {
  if (!workspaceDir || !contextPaths?.length) return null;
  const problems = await verifyContextPaths(contextPaths, workspaceDir);
  return problems.length > 0 ? textResult(formatContextPathProblems(problems)) : null;
}

// ── Tag audit surface ─────────────────────────────────────────────────

function buildListTagsTool(connection: ProjectToolsConnection, projectId: string) {
  return defineContractTool(
    listTagsContract,
    async () => {
      try {
        const tags = await connection.call("listProjectTags", { projectId });
        return textResult(JSON.stringify(tags, null, 2));
      } catch (error) {
        return errText("Failed to list tags", error);
      }
    },
    { annotations: { readOnlyHint: true } },
  );
}

function buildGetTagTool(connection: ProjectToolsConnection, projectId: string) {
  return defineContractTool(
    getTagContract,
    async ({ tag }) => {
      try {
        const detail = await connection.call("getProjectTag", { projectId, tag });
        return textResult(JSON.stringify(detail, null, 2));
      } catch (error) {
        return errText("Failed to get tag", error);
      }
    },
    { annotations: { readOnlyHint: true } },
  );
}

function buildCreateTagTool(
  connection: ProjectToolsConnection,
  projectId: string,
  workspaceDir?: string,
) {
  return defineTool(
    "create_tag",
    "Create a project tag. Include a crisp description (≤255 chars — the summary) and contextPaths (rule/doc/file/folder links agents auto-load when working on matching tasks); put the full spec in overview. Set parentTagIds to place the tag in the hierarchy right away. Every contextPath is checked against the repo checkout — a path that does not exist, or whose type does not match what is on disk, rejects the whole call. Fails if the name already exists.",
    {
      name: z.string().min(1).max(50),
      color: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/)
        .optional()
        .describe("#RRGGBB (default gray)"),
      description: z.string().max(TAG_DESCRIPTION_MAX).optional(),
      overview: z
        .string()
        .max(TAG_OVERVIEW_MAX)
        .optional()
        .describe("Full markdown glossary body — the term's spec"),
      overviewPath: z
        .string()
        .min(1)
        .max(500)
        .optional()
        .describe(
          "Repo file to source the overview from (base-branch content is served everywhere). A not-yet-merged path is fine — the tag serves the stored overview as fallback until the file lands.",
        ),
      parentTagIds: z
        .array(z.string())
        .max(25)
        .optional()
        .describe("Parent tag ids from list_tags (multi-parent DAG) to link at create time"),
      contextPaths: z.array(CONTEXT_PATH_SHAPE).max(20).optional(),
    },
    async ({ name, color, description, overview, overviewPath, parentTagIds, contextPaths }) => {
      const rejection = await rejectBadContextPaths(contextPaths, workspaceDir);
      if (rejection) return rejection;
      try {
        const result = await connection.call("createProjectTag", {
          projectId,
          name,
          color,
          description,
          overview,
          overviewPath,
          parentTagIds,
          contextPaths,
        });
        return textResult(`Tag created: ${result.id}`);
      } catch (error) {
        return errText("Failed to create tag", error);
      }
    },
  );
}

function buildUpdateTagTool(
  connection: ProjectToolsConnection,
  projectId: string,
  taskId?: string,
  workspaceDir?: string,
) {
  return defineTool(
    "update_tag",
    "Update a tag's name, color, description (≤255), markdown overview, parent tags, or contextPaths. contextPaths and parentTagIds are FULL replacements — include what you want to keep. ALWAYS pass a short reason; it lands in the tag's revision history (with your current card auto-stamped) so the team sees why the glossary changed. Every contextPath is checked against the repo checkout — a path that does not exist, or whose type does not match what is on disk, rejects the whole call and nothing is written.",
    {
      tagId: z.string().describe("Tag id from list_tags"),
      name: z.string().min(1).max(50).optional(),
      color: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/)
        .optional(),
      description: z.string().max(TAG_DESCRIPTION_MAX).optional(),
      overview: z
        .string()
        .max(TAG_OVERVIEW_MAX)
        .nullable()
        .optional()
        .describe(
          "Full markdown glossary body; null clears it. REJECTED while overviewPath is set — edit the sourced file in the repo instead",
        ),
      overviewPath: z
        .string()
        .min(1)
        .max(500)
        .nullable()
        .optional()
        .describe(
          "Repo file to source the overview from (null clears back to the stored overview). A not-yet-merged path is fine — the stored overview serves as fallback until the file lands on the base branch.",
        ),
      parentTagIds: z
        .array(z.string())
        .max(25)
        .optional()
        .describe("Full-set replacement of the tag's parent tags (multi-parent DAG)"),
      reason: z
        .string()
        .max(TAG_REASON_MAX)
        .optional()
        .describe("One line on why — shown in the tag's revision history"),
      contextPaths: z.array(CONTEXT_PATH_SHAPE).max(20).optional(),
    },
    async ({
      tagId,
      name,
      color,
      description,
      overview,
      overviewPath,
      parentTagIds,
      reason,
      contextPaths,
    }) => {
      const rejection = await rejectBadContextPaths(contextPaths, workspaceDir);
      if (rejection) return rejection;
      try {
        await connection.call("updateProjectTag", {
          projectId,
          tagId,
          name,
          color,
          description,
          overview,
          overviewPath,
          parentTagIds,
          reason,
          taskId,
          contextPaths,
        });
        return textResult(`Tag updated: ${tagId}`);
      } catch (error) {
        return errText("Failed to update tag", error);
      }
    },
  );
}

function buildCreateSuggestionTool(connection: ProjectToolsConnection, projectId: string) {
  return defineContractTool(createSuggestionContract, async ({ title, description, tag_names }) => {
    try {
      const result = await connection.call("createProjectSuggestion", {
        projectId,
        title,
        description,
        tagNames: tag_names,
      });
      return textResult(
        result.merged
          ? `Merged into existing suggestion ${result.mergedIntoId ?? result.id} (id: ${result.id})`
          : `Suggestion created: ${result.id}`,
      );
    } catch (error) {
      return errText("Failed to create suggestion", error);
    }
  });
}

function buildPostToProjectChatTool(connection: ProjectToolsConnection, projectId: string) {
  return defineTool(
    "post_to_project_chat",
    "Post a markdown message to the PROJECT chat — use once at the end of an audit for the summary the team reads.",
    {
      message: z.string().min(1).max(20000),
      kind: z
        .enum(["tag_audit_summary"])
        .optional()
        .describe(
          "Set to 'tag_audit_summary' when posting a tag-audit summary so it is also saved to the persistent tag history",
        ),
    },
    async ({ message, kind }) => {
      try {
        await connection.call("postToProjectChat", { projectId, content: message, kind });
        return textResult("Posted to project chat");
      } catch (error) {
        return errText("Failed to post to project chat", error);
      }
    },
  );
}

// ── Task audit surface ────────────────────────────────────────────────

function buildGetProjectTaskTool(connection: ProjectToolsConnection, projectId: string) {
  return defineTool(
    "get_project_task",
    "Fetch any task in the project by id or slug: title, description, plan, status, and metadata. The audit evidence trail starts here.",
    {
      taskId: z.string().describe("Task id or slug"),
    },
    async ({ taskId }) => {
      try {
        const task = await connection.call("getProjectTask", { projectId, taskId });
        return textResult(JSON.stringify(task, null, 2));
      } catch (error) {
        return errText("Failed to get task", error);
      }
    },
    { annotations: { readOnlyHint: true } },
  );
}

function buildReadProjectTaskChatTool(connection: ProjectToolsConnection, projectId: string) {
  return defineTool(
    "read_project_task_chat",
    "Read any project task's chat messages (newest last). role 'user' rows are HUMAN turns; 'assistant'/'system' rows are agent posts and activity-log entries.",
    {
      taskId: z.string().describe("Task id or slug"),
      limit: z.number().int().min(1).max(200).optional().describe("Messages to fetch (default 50)"),
    },
    async ({ taskId, limit }) => {
      try {
        const chat = await connection.call("getProjectTaskChat", {
          projectId,
          taskId,
          limit: limit ?? 50,
        });
        return textResult(JSON.stringify(chat, null, 2));
      } catch (error) {
        return errText("Failed to read task chat", error);
      }
    },
    { annotations: { readOnlyHint: true } },
  );
}

function buildGetProjectTaskLogsTool(connection: ProjectToolsConnection, projectId: string) {
  return defineTool(
    "get_project_task_logs",
    "Read any project task's persisted agent event stream (message / tool_use / turn_end / error / completed). Turn boundaries are turn_end events. Entries are truncated to ~2KB each; max 500 per call.",
    {
      taskId: z.string().describe("Task id or slug"),
      limit: z.number().int().min(1).max(500).optional().describe("Entries to fetch (default 50)"),
      source: z
        .enum(["agent", "application"])
        .optional()
        .describe("Filter: 'agent' = model events (default useful for grading)"),
    },
    async ({ taskId, limit, source }) => {
      try {
        const logs = await connection.call("getProjectTaskCli", {
          projectId,
          taskId,
          limit: limit ?? 50,
          source,
        });
        return textResult(JSON.stringify(logs, null, 2));
      } catch (error) {
        return errText("Failed to get task logs", error);
      }
    },
    { annotations: { readOnlyHint: true } },
  );
}

const TURN_GRADE_SHAPE = z.object({
  turnIndex: z.number().int().min(0),
  phase: z.enum(["planning", "building", "human"]),
  grade: z.enum(["correct", "neutral", "blunder"]),
  reasoning: z.string(),
  eventType: z.string().describe('e.g. "message", "tool_use", "human_message"'),
  eventSummary: z.string().max(200).describe("≤120 chars of what happened this turn"),
});

const HUMAN_EVAL_SHAPE = z.object({
  messageIndex: z
    .number()
    .int()
    .min(0)
    .describe("Index into the task's human messages, oldest first"),
  rating: z.number().int().min(-1).max(1),
  reasoning: z.string(),
});

function buildReportTaskAuditResultTool(connection: ProjectToolsConnection, projectId: string) {
  return defineTool(
    "report_task_audit_result",
    "Persist one audited task's grades (call once per task after grading it). Pass error instead to mark the audit failed when the evidence is unusable.",
    {
      taskId: z.string().describe("The audited task's id (NOT slug)"),
      summary: z.string().describe("3-6 sentences: what went well, what was wasted"),
      turnGrades: z.array(TURN_GRADE_SHAPE),
      planningAccuracy: z.number().min(0).max(1).nullable(),
      buildingAccuracy: z.number().min(0).max(1).nullable(),
      humanAccuracy: z.number().min(0).max(1).nullable(),
      planningCorrect: z.number().int().min(0),
      planningNeutral: z.number().int().min(0),
      planningBlunder: z.number().int().min(0),
      buildingCorrect: z.number().int().min(0),
      buildingNeutral: z.number().int().min(0),
      buildingBlunder: z.number().int().min(0),
      humanCorrect: z.number().int().min(0),
      humanNeutral: z.number().int().min(0),
      humanBlunder: z.number().int().min(0),
      humanEvaluations: z.array(HUMAN_EVAL_SHAPE).optional(),
      suggestionIds: z.array(z.string()).describe("Suggestion ids filed for this task, or []"),
      auditCostUsd: z.number().nullable(),
      model: z.string().nullable().describe("The model you are running as"),
      error: z.string().optional().describe("Set ONLY to mark this task's audit failed"),
    },
    async (input) => {
      try {
        await connection.call("reportTaskAuditResult", {
          projectId,
          ...input,
          humanEvaluations: (input.humanEvaluations ?? []) as Array<{
            messageIndex: number;
            rating: -1 | 0 | 1;
            reasoning: string;
          }>,
        });
        return textResult(
          input.error
            ? `Audit for ${input.taskId} marked failed`
            : `Audit result saved for ${input.taskId}`,
        );
      } catch (error) {
        return errText("Failed to report audit result", error);
      }
    },
  );
}

// ── Card search ───────────────────────────────────────────────────────

/**
 * Tag-first card search across the whole project. The glossary is only useful
 * if you can go from a term to the cards that carry it, so this ships wherever
 * list_tags/get_tag do — card sessions included, not just headless audits.
 */
function buildSearchTasksTool(connection: ProjectToolsConnection, projectId: string) {
  return defineContractTool(
    searchTasksContract,
    async (params) => {
      try {
        const tasks = await connection.call("searchProjectTasks", { projectId, ...params });
        return textResult(JSON.stringify(tasks, null, 2));
      } catch (error) {
        return errText("Failed to search tasks", error);
      }
    },
    { annotations: { readOnlyHint: true } },
  );
}

// ── Assembly ──────────────────────────────────────────────────────────

/**
 * The glossary surface for TASK-mode card agents: read the project vocabulary
 * (list_tags / get_tag), find the cards that carry a term (search_tasks), and
 * maintain the vocabulary (update_tag) — the session's card is stamped into
 * every revision for provenance.
 */
export function buildGlossaryTools(
  connection: ProjectToolsConnection,
  projectId: string,
  taskId?: string,
  workspaceDir?: string,
) {
  return [
    buildListTagsTool(connection, projectId),
    buildGetTagTool(connection, projectId),
    buildSearchTasksTool(connection, projectId),
    buildUpdateTagTool(connection, projectId, taskId, workspaceDir),
  ];
}

/** The full project-scoped tool surface a headless (task-less) session gets. */
export function buildProjectTools(
  connection: ProjectToolsConnection,
  projectId: string,
  workspaceDir?: string,
) {
  return [
    buildListTagsTool(connection, projectId),
    buildGetTagTool(connection, projectId),
    buildSearchTasksTool(connection, projectId),
    buildCreateTagTool(connection, projectId, workspaceDir),
    buildUpdateTagTool(connection, projectId, undefined, workspaceDir),
    buildCreateSuggestionTool(connection, projectId),
    buildPostToProjectChatTool(connection, projectId),
    buildGetProjectTaskTool(connection, projectId),
    buildReadProjectTaskChatTool(connection, projectId),
    buildGetProjectTaskLogsTool(connection, projectId),
    buildReportTaskAuditResultTool(connection, projectId),
  ];
}
